import { useCallback, useEffect, useReducer } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { INVOICE_TYPES } from '@/consts/billing'
import { useUserStore } from '@/stores/user'
import type {
  CharityOrganization,
  InvoiceFormState,
  InvoiceStepState,
  InvoiceTitleApiData,
  InvoiceType,
} from '@/types/billing'
import { validateEmail, validateMobileBarcode } from '@/utils/billing'
import { debounce } from '@/utils/debounce'

// Action types for reducer
type InvoiceFormAction =
  | {
      type: 'SET_LOADING'
      payload: { key: keyof InvoiceStepState['loadingStates']; value: boolean }
    }
  | { type: 'SET_ERROR'; payload: { key: keyof InvoiceStepState['errors']; value: string | null } }
  | {
      type: 'SET_VALIDATION_ERROR'
      payload: { field: keyof InvoiceFormState; error: string | null }
    }
  | { type: 'SET_API_DATA'; payload: InvoiceTitleApiData }
  | { type: 'SET_MATCHED_ORGANIZATION'; payload: CharityOrganization | null }
  | { type: 'SET_INVOICE_TYPE'; payload: InvoiceType }
  | { type: 'SET_FIELD'; payload: { field: keyof InvoiceFormState; value: string } }
  | { type: 'RESET_FORM' }
  | { type: 'INITIALIZE_FROM_API'; payload: InvoiceTitleApiData }
  | { type: 'SET_CHARITY_SELECTION'; payload: { name: string; code: string } }

// Initial state
const createInitialState = (): InvoiceStepState => ({
  formData: {
    invoiceType: INVOICE_TYPES[0].value as InvoiceType,
    name: '',
    email: '',
    mobileBarcode: '',
    businessId: '',
    businessName: '',
    charityCode: '',
    charityName: '',
  },
  apiData: null,
  matchedOrganization: null,
  loadingStates: {
    fetchingTitle: false,
    searchingCharity: false,
    validatingForm: false,
  },
  errors: {
    apiError: null,
    searchError: null,
    validationErrors: {} as Record<keyof InvoiceFormState, string>,
  },
  formDataCache: {} as Record<InvoiceType, Partial<InvoiceFormState>>,
})

// Reducer function
const invoiceFormReducer = (
  state: InvoiceStepState,
  action: InvoiceFormAction,
): InvoiceStepState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loadingStates: {
          ...state.loadingStates,
          [action.payload.key]: action.payload.value,
        },
      }

    case 'SET_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.key]: action.payload.value,
        },
      }

    case 'SET_VALIDATION_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          validationErrors: {
            ...state.errors.validationErrors,
            [action.payload.field]: action.payload.error || '',
          },
        },
      }

    case 'SET_API_DATA':
      return {
        ...state,
        apiData: action.payload,
      }

    case 'SET_MATCHED_ORGANIZATION':
      return {
        ...state,
        matchedOrganization: action.payload,
        // Only update charityName when we find a match, preserve user input for charityCode
        formData: action.payload
          ? {
              ...state.formData,
              charityName: action.payload.name,
            }
          : {
              ...state.formData,
              charityName: '', // Clear charity name when no match
            },
      }

    case 'SET_INVOICE_TYPE': {
      const currentType = state.formData.invoiceType
      const newType = action.payload

      // Save current form data to cache before switching
      const updatedCache = {
        ...state.formDataCache,
        [currentType]: {
          name: state.formData.name,
          email: state.formData.email,
          mobileBarcode: state.formData.mobileBarcode,
          businessId: state.formData.businessId,
          businessName: state.formData.businessName,
          charityCode: state.formData.charityCode,
          charityName: state.formData.charityName,
        },
      }

      // Restore from cache if available
      const cachedData = updatedCache[newType] || {}

      return {
        ...state,
        formDataCache: updatedCache,
        formData: {
          invoiceType: newType,
          name: cachedData.name || '',
          email: cachedData.email || '',
          mobileBarcode: cachedData.mobileBarcode || '',
          businessId: cachedData.businessId || '',
          businessName: cachedData.businessName || '',
          charityCode: cachedData.charityCode || '',
          charityName: cachedData.charityName || '',
        },
        matchedOrganization: null,
        errors: {
          ...state.errors,
          searchError: null,
          validationErrors: {} as Record<keyof InvoiceFormState, string>,
        },
      }
    }

    case 'SET_FIELD':
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.payload.field]: action.payload.value,
        },
      }

    case 'RESET_FORM':
      return createInitialState()

    case 'INITIALIZE_FROM_API': {
      const apiData = action.payload
      const ezpayData = apiData?.invoiceTitle?.ezpay

      if (!ezpayData) return state

      // Map API data to form state
      const newFormData = { ...state.formData }
      let newInvoiceType = state.formData.invoiceType

      // Determine invoice type and set defaults
      if (ezpayData.donate_code) {
        newInvoiceType = 'donation'
        newFormData.name = ezpayData.name || ''
        newFormData.charityCode = ezpayData.donate_code
      } else if (ezpayData.type === 2 && ezpayData.UBN) {
        newInvoiceType = 'unified-business'
        newFormData.businessId = ezpayData.UBN
        newFormData.businessName = ezpayData.name
        newFormData.email = ezpayData.email
      } else if (ezpayData.carrier_type === 1 && ezpayData.carrier_num) {
        newInvoiceType = 'mobile-barcode'
        newFormData.name = ezpayData.name || ''
        newFormData.mobileBarcode = ezpayData.carrier_num.toUpperCase()
      } else if (ezpayData.email) {
        newInvoiceType = 'electronic'
        newFormData.name = ezpayData.name || ''
        newFormData.email = ezpayData.email
      }

      return {
        ...state,
        apiData: apiData,
        formData: {
          ...newFormData,
          invoiceType: newInvoiceType,
        },
        formDataCache: {
          ...state.formDataCache,
          [newInvoiceType]: { ...newFormData },
        },
      }
    }

    case 'SET_CHARITY_SELECTION':
      return {
        ...state,
        formData: {
          ...state.formData,
          charityCode: action.payload.code,
          charityName: action.payload.name,
        },
        matchedOrganization: {
          name: action.payload.name,
          donateCode: action.payload.code,
          unifiedCode: '', // This will be filled when we search for the organization
        },
        errors: {
          ...state.errors,
          searchError: null, // Clear search error when manually selected
        },
      }

    default:
      return state
  }
}

export function useInvoiceFormState() {
  const [state, dispatch] = useReducer(invoiceFormReducer, createInitialState())
  const userStore = useUserStore()
  const { t } = useTranslation('billing')

  // Search donate organizations by code
  const searchDonateOrganization = useCallback(async (code: string) => {
    if (!code.trim()) {
      dispatch({ type: 'SET_MATCHED_ORGANIZATION', payload: null })
      dispatch({ type: 'SET_ERROR', payload: { key: 'searchError', value: null } })
      return
    }

    dispatch({ type: 'SET_LOADING', payload: { key: 'searchingCharity', value: true } })
    dispatch({ type: 'SET_ERROR', payload: { key: 'searchError', value: null } })

    try {
      const response = await API.getDonateOrgs({
        page: 1,
        per_page: 100,
        key: code.trim(),
      })

      const organizations = Array.isArray(response.organizations) ? response.organizations : []
      const exactMatch = organizations.find((org) => org.donateCode === code.trim())

      if (exactMatch) {
        dispatch({ type: 'SET_MATCHED_ORGANIZATION', payload: exactMatch })
      } else {
        dispatch({ type: 'SET_MATCHED_ORGANIZATION', payload: null })
        dispatch({ type: 'SET_ERROR', payload: { key: 'searchError', value: 'Not Found' } })
      }
    } catch (error) {
      console.error('Failed to search donate organizations:', error)
      dispatch({ type: 'SET_MATCHED_ORGANIZATION', payload: null })
      dispatch({ type: 'SET_ERROR', payload: { key: 'searchError', value: 'Search failed' } })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'searchingCharity', value: false } })
    }
  }, [])

  // Debounced search function
  const debouncedSearchDonation = useCallback(
    debounce((code: string) => {
      searchDonateOrganization(code)
    }, 300),
    [searchDonateOrganization],
  )

  // Fetch invoice title data
  const fetchInvoiceTitle = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: { key: 'fetchingTitle', value: true } })
    dispatch({ type: 'SET_ERROR', payload: { key: 'apiError', value: null } })

    try {
      const data = await API.getInvoiceTitle()
      dispatch({ type: 'SET_API_DATA', payload: data })
      dispatch({ type: 'INITIALIZE_FROM_API', payload: data })

      // If there's a donation code, search for organization
      const donateCode = data?.invoiceTitle?.ezpay?.donate_code
      if (donateCode) {
        searchDonateOrganization(donateCode)
      }
    } catch (error) {
      console.error('Failed to fetch invoice title:', error)
      dispatch({
        type: 'SET_ERROR',
        payload: { key: 'apiError', value: 'Failed to load invoice data' },
      })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'fetchingTitle', value: false } })
    }
  }, [searchDonateOrganization])

  // Handle field changes
  const handleFieldChange = useCallback(
    (field: keyof InvoiceFormState, value: string) => {
      dispatch({ type: 'SET_FIELD', payload: { field, value } })

      // Special handling for email validation
      if (field === 'email') {
        if (!value || !value.trim()) {
          // Clear error when empty (allowed)
          dispatch({ type: 'SET_VALIDATION_ERROR', payload: { field: 'email', error: null } })
        } else if (value.length > 50) {
          // Length error has priority
          dispatch({
            type: 'SET_VALIDATION_ERROR',
            payload: { field: 'email', error: t('invoiceModal.validation.emailTooLong') },
          })
        } else if (!validateEmail(value)) {
          // Format error
          dispatch({
            type: 'SET_VALIDATION_ERROR',
            payload: { field: 'email', error: t('invoiceModal.validation.emailInvalid') },
          })
        } else {
          // Valid email
          dispatch({ type: 'SET_VALIDATION_ERROR', payload: { field: 'email', error: null } })
        }
      }

      // Show an inline format hint for mobile barcodes as soon as the user
      // enters a non-empty value. Empty values are handled by form validation.
      if (field === 'mobileBarcode') {
        dispatch({
          type: 'SET_VALIDATION_ERROR',
          payload: {
            field: 'mobileBarcode',
            error:
              value.trim() !== '' && !validateMobileBarcode(value)
                ? t('invoiceModal.validation.mobileBarcodeInvalid')
                : null,
          },
        })
      }

      // Special handling for charity code
      if (field === 'charityCode') {
        debouncedSearchDonation(value)
      }
    },
    [debouncedSearchDonation, t],
  )

  // Handle invoice type change
  const handleInvoiceTypeChange = useCallback(
    (type: InvoiceType) => {
      dispatch({ type: 'SET_INVOICE_TYPE', payload: type })

      // Auto-fill email for electronic and unified-business invoice if userStore.name is a valid email
      if (
        (type === 'electronic' || type === 'unified-business') &&
        userStore.name &&
        validateEmail(userStore.name)
      ) {
        dispatch({ type: 'SET_FIELD', payload: { field: 'email', value: userStore.name } })
      }
    },
    [userStore.name],
  )

  // Handle charity selection from modal
  const handleCharitySelection = useCallback((charity: { name: string; code: string }) => {
    dispatch({ type: 'SET_CHARITY_SELECTION', payload: charity })
  }, [])

  // Initialize on mount
  useEffect(() => {
    fetchInvoiceTitle()
  }, [fetchInvoiceTitle])

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearchDonation.cancel()
    }
  }, [debouncedSearchDonation])

  return {
    state,
    actions: {
      handleFieldChange,
      handleInvoiceTypeChange,
      handleCharitySelection,
      searchDonateOrganization,
      fetchInvoiceTitle,
    },
  }
}
